import { useCallback, useEffect, useRef, useState } from "react";
import { connectionHubApi } from "../api/connectionHub.api";
import type {
  ConnectionInput,
  DraftView,
} from "../api/connectionHub.types";
import { connectionHubLogger } from "../utils/logger";

const AUTOSAVE_DELAY_MS = 600;

const hasDraftContent = (input: ConnectionInput): boolean =>
  Boolean(input.email?.trim() || input.password || input.otpMode || input.inboxOwner);

interface UseDraftAutosaveOptions {
  launchId: number;
  agencyClientId: number | null;
  inputs: ConnectionInput[];
  onSaved: (drafts: DraftView[]) => void;
}

export interface DraftAutosaveResult {
  saving: boolean;
  dirty: boolean;
  flush: () => Promise<void>;
  flushCard: (input: ConnectionInput) => Promise<boolean>;
}

export const useDraftAutosave = ({
  launchId,
  agencyClientId,
  inputs,
  onSaved,
}: UseDraftAutosaveOptions): DraftAutosaveResult => {
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const pendingCountRef = useRef(0);
  const generationRef = useRef(0);
  const initializedLaunchRef = useRef<number | null>(null);
  const currentInputsRef = useRef(inputs);
  const currentSnapshotRef = useRef(JSON.stringify(inputs));
  const savedSnapshotRef = useRef(JSON.stringify(inputs));
  const onSavedRef = useRef(onSaved);

  const snapshot = JSON.stringify(inputs);

  onSavedRef.current = onSaved;
  currentInputsRef.current = inputs;
  currentSnapshotRef.current = snapshot;

  const enqueue = useCallback(
    async (channels: ConnectionInput[], snapshot?: string): Promise<boolean> => {
      const payload = channels.filter(hasDraftContent);
      if (!payload.length) {
        if (snapshot) savedSnapshotRef.current = snapshot;
        setDirty(currentSnapshotRef.current !== savedSnapshotRef.current);
        return true;
      }
      const generation = generationRef.current;
      pendingCountRef.current += 1;
      setSaving(true);
      const request = queueRef.current
        .catch(() => undefined)
        .then(() => connectionHubApi.saveDrafts({ channels: payload, agencyClientId }));
      queueRef.current = request;
      try {
        const result = await request;
        if (generation !== generationRef.current) return false;
        if (snapshot) savedSnapshotRef.current = snapshot;
        setDirty(currentSnapshotRef.current !== savedSnapshotRef.current);
        onSavedRef.current(result.drafts);
        return true;
      } catch (caught) {
        connectionHubLogger.error(caught, { errorCode: "CONNECTION_DRAFT_AUTOSAVE_FAILED" });
        if (generation === generationRef.current) setDirty(true);
        return false;
      } finally {
        pendingCountRef.current = Math.max(0, pendingCountRef.current - 1);
        if (generation === generationRef.current) setSaving(pendingCountRef.current > 0);
      }
    },
    [agencyClientId]
  );

  const flush = useCallback(async (): Promise<void> => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    const snapshot = currentSnapshotRef.current;
    if (snapshot === savedSnapshotRef.current) {
      await queueRef.current.catch(() => undefined);
      return;
    }
    await enqueue(currentInputsRef.current, snapshot);
  }, [enqueue]);

  // Carries the snapshot so the debounce does not re-arm and send the same payload again.
  const flushCard = useCallback(
    (input: ConnectionInput): Promise<boolean> => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      return enqueue([input], currentSnapshotRef.current);
    },
    [enqueue]
  );

  useEffect(() => {
    if (initializedLaunchRef.current !== launchId) {
      initializedLaunchRef.current = launchId;
      savedSnapshotRef.current = currentSnapshotRef.current;
      setDirty(false);
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    const changed = snapshot !== savedSnapshotRef.current;
    setDirty(changed);
    if (!changed || !currentInputsRef.current.length) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      void enqueue(currentInputsRef.current, currentSnapshotRef.current);
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Keyed on the serialized snapshot: `inputs` is a fresh array each render and would re-arm constantly.
  }, [enqueue, launchId, snapshot]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return { saving, dirty, flush, flushCard };
};
